import { Alert } from "@mui/material";
import type { ComponentProps } from "react";
import { isDefined } from "@entrilia/shared/utilities/typeHelper";
import type { Field, FieldIdToValueMap } from "../../../../api/types/objectTypes";
import RelatedObjectsGroup from "./RelatedObjectsGroup";
import type { RelatedObjectRowModel } from "./relatedObjectValuesState";
import { getLinesDifference, type InvoiceCardPreviewConfig, type SummaryDifference } from "./invoiceCardPreviewState";
import InvoiceLinesSummary from "./InvoiceLinesSummary";

const getDifferenceRow = (
  summaryDifference: SummaryDifference | undefined,
  fields: Field[],
  fieldValues: FieldIdToValueMap,
  rows: RelatedObjectRowModel[]
) => {
  const field = fields.find((f) => f.id === summaryDifference?.amountFieldId);
  if (!summaryDifference || !field) {
    return undefined;
  }

  const lineAmounts = rows.map((row) => row[summaryDifference.lineAmountFieldId]);
  const value = getLinesDifference(fieldValues[field.id], lineAmounts);
  return value === undefined ? undefined : { label: summaryDifference.label, field, value };
};

interface Props extends ComponentProps<typeof RelatedObjectsGroup> {
  invoiceFields: Field[];
  invoiceFieldValues: FieldIdToValueMap;
  cardPreview?: InvoiceCardPreviewConfig;
  previewFieldValues?: FieldIdToValueMap;
  hasPreviewFailed: boolean;
}

const InvoiceRelatedObjectsGroup = ({
  invoiceFields,
  invoiceFieldValues,
  cardPreview,
  previewFieldValues,
  hasPreviewFailed,
  cellDecorations,
  noOptionsTexts,
  onUserRowChange,
  ...groupProps
}: Props) => {
  const { layout, rows } = groupProps;
  const isPreviewed = layout.objectType === cardPreview?.relatedObjectType;
  const summaryFields = layout.parentSummaryFieldIds
    .map((id) => invoiceFields.find((f) => f.id === id))
    .filter(isDefined());
  const summaryValues = { ...invoiceFieldValues, ...previewFieldValues };
  const hasSummaryValues = summaryFields.some((f) => summaryValues[f.id] != null);
  const difference = isPreviewed
    ? getDifferenceRow(cardPreview.summaryDifference, invoiceFields, invoiceFieldValues, rows)
    : undefined;

  return (
    <>
      <RelatedObjectsGroup
        {...groupProps}
        cellDecorations={isPreviewed ? cellDecorations : undefined}
        columnLayouts={isPreviewed ? cardPreview.relatedColumnLayouts : undefined}
        noOptionsTexts={isPreviewed ? noOptionsTexts : undefined}
        onUserRowChange={isPreviewed ? onUserRowChange : undefined}
      >
        {rows.length > 0 && (hasSummaryValues || difference) && (
          <InvoiceLinesSummary
            fields={hasSummaryValues ? summaryFields : []}
            fieldValues={summaryValues}
            difference={difference}
          />
        )}
      </RelatedObjectsGroup>
      {isPreviewed && hasPreviewFailed && (
        <Alert severity="warning">
          Couldn&apos;t check the changes. Values that fill in automatically may be missing — review them before saving.
        </Alert>
      )}
    </>
  );
};

export default InvoiceRelatedObjectsGroup;
